/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shield, UserRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import TurnstileWidget from "@/components/TurnstileWidget";
import { getClientFingerprint } from "@/lib/fingerprint";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  // v72 — child-scoped invite token comes in via URL
  // (?invite_token=…) and bypasses the legacy invite_code flow.
  const inviteToken = params.get("invite_token") || "";
  const [tab, setTab] = useState(
    inviteToken || params.get("mode") === "register" ? "register" : "login",
  );
  const [busy, setBusy] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPw, setLoginPw] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needTotp, setNeedTotp] = useState(false);

  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPw, setRPw] = useState("");
  // v72 — preview block shown above the register form when an
  // invite_token is present in the URL. Lets the new co-parent see
  // exactly which household + children they're being invited to BEFORE
  // creating an account.
  const [invitePreview, setInvitePreview] = useState(null);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (!inviteToken) return;
    setTab("register");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/lookup/${encodeURIComponent(inviteToken)}`);
        if (!cancelled) setInvitePreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(
            err.response?.data?.detail || "This invite link is invalid or expired.",
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  const [archivedView, setArchivedView] = useState(null); // { can_restore, days_remaining, archived_at, restore_token, email }
  const [restoring, setRestoring] = useState(false);

  // v77.97 — Turnstile token + browser fingerprint for the login &
  // register forms. Each tab carries its own token (Turnstile widgets
  // are independent per render). We refuse submit while the token is
  // empty; refs let us reset the widget on a failed submit.
  const [loginTsToken, setLoginTsToken] = useState("");
  const [registerTsToken, setRegisterTsToken] = useState("");
  const loginTsRef = useRef(null);
  const registerTsRef = useRef(null);
  // v77.99.11 — Whether the Turnstile gate is in play for this build.
  // Mirrors the widget's own check (see TurnstileWidget.resolveSiteKey)
  // so we can disable submit buttons when verification is required but
  // skip the gate entirely on environments without a configured site
  // key (CI, local dev without secrets). Hostname-aware so preview
  // hosts use the always-passes test key and production uses the real
  // one — both paths fire `onToken` so the gate clears on both.
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const doLogin = async (e) => {
    e.preventDefault();
    // v77.99.11 — Belt-and-braces guard. Mobile keyboards (Chrome's
    // "Go" button on the password field) and IME submit gestures can
    // submit a <form> directly, bypassing the disabled Submit button.
    // Reject up-front so we never POST an empty turnstile_token, and
    // surface a hint that points at the actual problem (missing widget,
    // hostname not whitelisted, script blocked) instead of a generic
    // "Verification failed" toast.
    if (turnstileEnabled && !loginTsToken) {
      toast.error(
        "Verification not ready",
        {
          description: "Please tick the 'Verify you are human' box before signing in. If you can't see it, refresh the page or check that your network isn't blocking challenges.cloudflare.com.",
        },
      );
      return;
    }
    setBusy(true);
    try {
      const result = await login(loginEmail, loginPw, totpCode, {
        turnstile_token: loginTsToken,
      });
      // v77.18 — Archived account branch.
      if (result?.archived) {
        setArchivedView(result);
        setBusy(false);
        return;
      }
      const u = result?.user || result;
      // Reactivate-in-one-click — Iter 123.reactivate.
      // Locked users go straight to a focused reactivate surface so
      // the flow is: sign in → one click → Stripe form → back in
      // read/write inside the same session. Everyone else lands on
      // their usual home.
      const planName = u?.plan?.plan;
      const isLocked = planName === "locked" || u?.plan?.is_locked === true;
      toast.success(result?.welcome_back ? "Welcome back, your account is active again." : "Welcome back");
      if (isLocked) {
        navigate("/app/upgrade?reactivate=1");
      } else {
        navigate(u?.role === "mediator" ? "/app/third-party" : (u?.is_primary_admin ? "/app/admin" : "/app/dashboard"));
      }
    } catch (err) {
      // Coerce `detail` to a string before pattern-matching. FastAPI
      // returns three different shapes:
      //   1. plain string  ("Invalid credentials")          → use as-is
      //   2. Pydantic array of validation errors            → join msgs
      //   3. structured dict (e.g. turnstile_failed)        → use .message
      // Earlier we fell through dict-shaped details to a generic
      // "Sign in failed" toast, which masked the actual reason
      // (most commonly a missing Turnstile verification token) and
      // left the user with no actionable guidance.
      const raw = err.response?.data?.detail;
      let detail;
      if (typeof raw === "string") {
        detail = raw;
      } else if (Array.isArray(raw)) {
        detail = raw.map((x) => x?.msg || "").filter(Boolean).join(", ") || "Sign in failed";
      } else if (raw && typeof raw === "object") {
        detail = raw.message || raw.error || "Sign in failed";
      } else {
        detail = "Sign in failed";
      }
      // v77.99.140 (iter 124.turnstile-diag) — When Cloudflare rejects
      // the login token, decode `error_codes` into a targeted message.
      // Previously every failure showed the generic "Verification
      // failed. Please refresh and try again" toast, which sent admins
      // hunting through the wrong parts of the stack. Now the exact
      // Cloudflare error surfaces one useful line.
      if (raw && typeof raw === "object" && raw.error === "turnstile_failed") {
        const codes = Array.isArray(raw.error_codes) ? raw.error_codes : [];
        if (codes.includes("bad-request") || codes.includes("invalid-input-hostname")) {
          detail = "Sign-in is temporarily unavailable — the login hostname isn't authorised. Please contact support (Cloudflare Turnstile Allowed Hostnames).";
        } else if (codes.includes("invalid-input-secret") || codes.includes("missing-input-secret")) {
          detail = "Sign-in is temporarily unavailable — verification secret misconfigured. Please contact support.";
        } else if (codes.includes("timeout-or-duplicate")) {
          detail = "Your verification token has already been used. Please refresh the page and try again.";
        }
      }
      if (detail.includes("2FA")) {
        setNeedTotp(true);
        toast.info("Enter your 2FA code");
      } else if (err.response?.status === 410) {
        toast.error("This account has been permanently deleted.");
      } else {
        toast.error(detail);
      }
      try { loginTsRef.current?.reset(); setLoginTsToken(""); } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async () => {
    if (!archivedView?.restore_token) return;
    setRestoring(true);
    try {
      // v77.91 — Use `window.location.origin` for the restore call so it
      // works on every environment (preview, app.coparentconsole.com,
      // any future custom domain) without depending on the build-time
      // `REACT_APP_BACKEND_URL` matching whatever URL the user is
      // currently sitting on. The restore_token is short-lived and
      // already authoritative — same-origin is the right scope.
      await fetch(`${window.location.origin}/api/auth/restore`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${archivedView.restore_token}`,
        },
      }).then((r) => {
        if (!r.ok) throw new Error("Restore failed");
      });
      toast.success("Account restored");
      // Now log in for real with the original password
      const result = await login(loginEmail, loginPw);
      const u = result?.user || result;
      // Mirror the doLogin redirect: a restored account whose trial +
      // grace have both elapsed should land straight on the reactivate
      // surface instead of the dashboard.
      const isLocked = u?.plan?.plan === "locked" || u?.plan?.is_locked === true;
      if (isLocked) {
        navigate("/app/upgrade?reactivate=1");
      } else {
        navigate(u?.role === "mediator" ? "/app/third-party" : (u?.is_primary_admin ? "/app/admin" : "/app/dashboard"));
      }
    } catch (err) {
      toast.error("Restore failed — please try again");
    } finally {
      setRestoring(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (turnstileEnabled && !registerTsToken) {
      toast.error(
        "Verification not ready",
        {
          description: "Please tick the 'Verify you are human' box before creating your account. If you can't see it, refresh the page or check that your network isn't blocking challenges.cloudflare.com.",
        },
      );
      return;
    }
    setBusy(true);
    try {
      const fp = await getClientFingerprint();
      const u = await register({
        name: rName,
        email: rEmail,
        password: rPw,
        // v75 — child-scoped tokens are the ONLY supported invite path.
        // Legacy household-level invite codes have been retired.
        invite_token: inviteToken || null,
        turnstile_token: registerTsToken,
        client_fingerprint: fp,
      });
      // v77.96 — Fire GA4 `trial_started_success` right after a
      // successful parent registration so we can close the
      // Landing → Pricing → Trial conversion funnel in GA4. We
      // exclude mediators (invited professionals don't run the
      // trial funnel) and co-parents joining via an invite_token
      // (they join an existing household, the primary parent's
      // trial is what matters). `utm_source` is carried through
      // from the URL so GA4 can attribute which CTA path led to
      // this successful signup (matches the `cta_pricing_signup`
      // link which appends `utm_source=pricing` / `pricing_final`).
      const isParentPrimarySignup =
        u?.role !== "mediator" && !inviteToken;
      if (isParentPrimarySignup) {
        trackEvent("trial_started_success", {
          utm_source: params.get("utm_source") || "direct",
        });
      }
      toast.success("Account created");
      // v77.97 — Primary parent registrations now require email
      // verification. Send them to the "verify your email" screen
      // first; trial-gated /app/* routes still let them in (the
      // trial is active) but a banner reminds them. Co-parents and
      // mediators are pre-verified server-side.
      const needsVerification = u && u.is_email_verified === false;
      if (needsVerification) {
        navigate("/verify-pending", { replace: true });
        return;
      }
      navigate(u?.role === "mediator" ? "/app/third-party" : (u?.is_primary_admin ? "/app/admin" : "/app/dashboard"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" ? detail : "Sign up failed");
      toast.error(msg);
      try { registerTsRef.current?.reset(); setRegisterTsToken(""); } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7F8] dark:bg-[#0F1517] text-[#2E3A3F] dark:text-[#E8EEF0] flex flex-col">
      <header className="border-b border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#0F1517]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="auth-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg text-[#2E3A3F] dark:text-[#E8EEF0]">CoParent Console</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {archivedView ? (
          <div
            className="w-full max-w-md border border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#1A2225] rounded-md p-6 md:p-8"
            data-testid="auth-archived-view"
          >
            <div className="w-12 h-12 rounded-full bg-[#FBEFED] inline-flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-[#C0291D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            <h2 className="font-heading text-xl font-semibold text-[#2E3A3F] dark:text-[#E8EEF0]">
              Your account was archived
            </h2>
            <p className="text-sm text-[#5C5C5C] dark:text-[#A8B6BB] mt-2 leading-relaxed">
              {archivedView.can_restore ? (
                <>
                  Your CoParent Console account was archived due to inactivity.
                  You can restore it within the next{" "}
                  <strong>{archivedView.days_remaining} day{archivedView.days_remaining === 1 ? "" : "s"}</strong>.
                  Once restored, all your data is exactly as you left it.
                </>
              ) : (
                <>
                  Your CoParent Console account was archived due to inactivity
                  more than 30 days ago and is now scheduled for permanent
                  removal. We&apos;re unable to restore it from this screen.
                </>
              )}
            </p>
            {archivedView.can_restore ? (
              <Button
                onClick={doRestore}
                disabled={restoring}
                className="w-full mt-6 bg-[#1F9FA4] hover:bg-[#198185] text-white"
                data-testid="auth-restore-button"
              >
                {restoring ? "Restoring…" : "Restore my account"}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setArchivedView(null)}
                className="w-full mt-6"
              >
                Back to sign in
              </Button>
            )}
            {archivedView.can_restore && (
              <button
                type="button"
                onClick={() => setArchivedView(null)}
                className="mt-3 w-full text-xs text-[#6F828A] hover:text-[#2E3A3F]"
                data-testid="auth-archived-back"
              >
                Cancel
              </button>
            )}
          </div>
        ) : (
        <div className="w-full max-w-md border border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#1A2225] rounded-md p-6 md:p-8">
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-semibold text-[#2E3A3F] dark:text-[#E8EEF0]">Access your co-parenting account</h1>
            <p className="text-sm text-[#6F828A] dark:text-[#A8B6BB] mt-1">Sign in or create an account to continue.</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full rounded-sm bg-[#E8F0F1] dark:bg-[#1F2A2D]">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <div
                className="mb-4 rounded-sm border border-[#D9E1E3] bg-[#F7FAFA] px-3 py-2 text-xs text-[#3F535A]"
                data-testid="login-reactivate-hint"
              >
                <strong>Coming back after a break?</strong> Your data is safe. If your account is locked, signing in below takes you straight to a one-click reactivate flow.
              </div>
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="login-pw">Password</Label>
                  <PasswordInput id="login-pw" value={loginPw} onChange={(e) => setLoginPw(e.target.value)} required data-testid="login-password-input" />
                </div>
                {needTotp && (
                  <div data-testid="totp-field">
                    <Label htmlFor="login-totp">Authenticator code</Label>
                    <Input id="login-totp" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={totpCode} onChange={(e) => setTotpCode(e.target.value)} placeholder="6-digit code" data-testid="login-totp-input" />
                  </div>
                )}
                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-xs text-[#6F828A] hover:text-[#2E3A3F]" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <TurnstileWidget
                  ref={loginTsRef}
                  action="login"
                  testId="login-turnstile"
                  onToken={setLoginTsToken}
                />
                {/* v77.99.11 — Block submit until Turnstile has issued
                    a token. Previously the button was always enabled,
                    so users who clicked "Sign in" before completing the
                    "Verify you are human" challenge hit a 400 from the
                    backend and saw a generic "Sign in failed" toast.
                    Disabling the button keeps the gate visible and
                    actionable. The widget is skipped entirely when no
                    site key is configured, so we only enforce the gate
                    when the widget is actually rendering. */}
                {turnstileEnabled && !loginTsToken && (
                  <p className="text-[11px] text-[#6F828A] dark:text-[#A8B6BB]" data-testid="login-turnstile-hint">
                    Tick the verification box above to enable sign in.
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={busy || (turnstileEnabled && !loginTsToken)}
                  className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
                  data-testid="login-submit-button"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              {invitePreview && (
                <div
                  className="mb-4 border border-[#A7D8DA] bg-[#E0F2F2] rounded-sm p-3 text-sm"
                  data-testid="invite-preview"
                >
                  <div className="font-medium text-[#116B6E] inline-flex items-center gap-2">
                    <UserRound className="w-4 h-4" />
                    {invitePreview.inviter_name || "Your co-parent"} invited you
                  </div>
                  <p className="text-xs text-[#0F5658] mt-1">
                    You&apos;ll be set up as a co-parent for{" "}
                    <span className="font-medium">{(invitePreview.child_names || []).join(", ") || "shared parenting"}</span>.
                    You&apos;ll only see information for the children listed above.
                  </p>
                </div>
              )}
              {!invitePreview && (
                <div
                  className="mb-4 rounded-sm border border-[#A7D8DA] bg-[#E0F2F2] px-3 py-2 text-xs text-[#0F5658]"
                  data-testid="register-trial-note"
                >
                  <strong>30-day free trial · no card required.</strong> Explore every
                  co-parenting module — messages, calendar, expenses, journal, contact
                  visits and more.
                </div>
              )}
              {previewError && (
                <div
                  className="mb-4 border border-[#F3D4D9] bg-[#FDF5F6] rounded-sm p-3 text-sm text-[#8C2A36]"
                  data-testid="invite-preview-error"
                >
                  {previewError}
                </div>
              )}
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label htmlFor="r-name">Full name</Label>
                  <Input id="r-name" value={rName} onChange={(e) => setRName(e.target.value)} required data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="r-email">Email</Label>
                  <Input id="r-email" type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} required data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="r-pw">Password</Label>
                  <PasswordInput id="r-pw" minLength={6} value={rPw} onChange={(e) => setRPw(e.target.value)} required data-testid="register-password-input" />
                </div>
                {/* v75 — household invite codes have been retired. The
                    only invite path is a per-child invite_token, which
                    is delivered to the user as part of the URL. */}
                <TurnstileWidget
                  ref={registerTsRef}
                  action="register"
                  testId="register-turnstile"
                  onToken={setRegisterTsToken}
                />
                {turnstileEnabled && !registerTsToken && (
                  <p className="text-[11px] text-[#6F828A] dark:text-[#A8B6BB]" data-testid="register-turnstile-hint">
                    Tick the verification box above to enable account creation.
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={busy || (turnstileEnabled && !registerTsToken)}
                  className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
                  data-testid="register-submit-button"
                >
                  {busy ? "Creating…" : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          {/* v77.41 — Subtle Irish ownership reassurance below the
              auth form. Centred, light grey, secondary to the
              primary CTA. */}
          <p
            className="text-xs text-[#A3B0B5] dark:text-[#7A8A91] text-center mt-6"
            data-testid="auth-irish-ownership-line"
          >
            An Irish-made platform, built with families in mind.
          </p>
        </div>
        )}
      </main>
    </div>
  );
}
